import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { isUndefined } from 'lodash'
import { isAfter } from '@e3dc-react/shell/Libs/date'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useUser } from 'src/Hooks/useUser'
import { confirmEmailAddressChange } from 'src/Libs/user/api/user.api'
import { Typography } from '@e3dc-react/shell/Elements/Typography'

interface Props {
  changeEmailToken: string
  removeChangeEmailToken: () => void
  isUsernameChange?: boolean
}

const ChangeEmailConfirmation: React.VFC<Props> = ({ changeEmailToken, removeChangeEmailToken, isUsernameChange }) => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const [isLoading, setIsLoading] = useState(false)
  const { userData, contactData } = useUser()
  const tokenIsExpired = useMemo(
    () => contactData?.emailChangeExpireDate && isAfter(new Date(), new Date(contactData.emailChangeExpireDate)),
    [contactData]
  )
  const dispatch = useDispatch()
  const confirmationNotPossible = tokenIsExpired || !contactData?.emailChange

  const onConfirmEmailAddressChange = (userId: number): void => {
    try {
      setIsLoading(true)
      confirmEmailAddressChange(changeEmailToken, userId, authFetch)
      enqueueSuccessStack(t('user.changeEmailDialog.changeEmailConfirmationSuccess'))
      removeChangeEmailToken()
      if (isUsernameChange) {
        navigate('/logout')
      } else {
        navigate('/user')
      }
      dispatch(refreshApiView())
    } catch (e) {
      console.error(e)
      enqueueErrorStack(t('user.changeEmailDialog.changeEmailConfirmationError'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      maxWidth="sm"
      title={t('user.changeEmailDialog.title')}
      onClose={() => {
        navigate('/')
      }}
      actions={{
        primary: {
          name: t('user.company.invitation.accept.buttonText'),
          onClick: () => (userData?.ID ? onConfirmEmailAddressChange(userData?.ID) : undefined),
          isLoading,
          disabled: isUndefined(userData?.ID) || confirmationNotPossible,
        },
        secondary: {
          name: t('common.cancel'),
          onClick: () => {
            removeChangeEmailToken()
            navigate('/user')
          },
        },
      }}
    >
      {confirmationNotPossible ? (
        <Typography>user.changeEmailDialog.changeEmailTokenExpired</Typography>
      ) : isUsernameChange ? (
        <Typography values={{ newEmail: contactData?.emailChange ?? '' }}>{'user.change-email-dialog.confirm-email-username-change.text'}</Typography>
      ) : (
        <Typography values={{ newEmail: contactData?.emailChange ?? '' }}>{'user.change-email-dialog.confirm-change-email.text'}</Typography>
      )}
    </Dialog>
  )
}

export default ChangeEmailConfirmation
